// Helper functions for persisting selected products across browser sessions

const STORAGE_KEY = 'alibabaSelectedProducts';
const EXPIRY_HOURS = 24; // Data expires after 24 hours

/**
 * Get selected products from localStorage
 * @returns {Object} selectedProductsByKeyword object
 */
export const getStoredSelectedProducts = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return {};

    const data = JSON.parse(stored);

    // Check if data has expired
    if (data.expiry && Date.now() > data.expiry) {
      localStorage.removeItem(STORAGE_KEY);
      return {};
    }

    return data.selectedProductsByKeyword || {};
  } catch (error) {
    console.error('Error reading stored selected products:', error);
    localStorage.removeItem(STORAGE_KEY);
    return {};
  }
};

/**
 * Save selected products to localStorage
 * @param {Object} selectedProductsByKeyword - Products grouped by keyword
 */
export const storeSelectedProducts = (selectedProductsByKeyword) => {
  try {
    const data = {
      selectedProductsByKeyword,
      expiry: Date.now() + (EXPIRY_HOURS * 60 * 60 * 1000), // 24 hours from now
      timestamp: Date.now()
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    // console.log('Selected products stored successfully:', selectedProductsByKeyword);
  } catch (error) {
    console.error('Error storing selected products:', error);
  }
};

/**
 * Clear stored selected products
 */
export const clearStoredSelectedProducts = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    // console.log('Stored selected products cleared');
  } catch (error) {
    // console.error('Error clearing stored selected products:', error);
  }
};

/**
 * Add product to stored selection for a specific keyword
 * @param {string} keyword - Search keyword
 * @param {Object} product - Product to add
 */
export const addProductToStorage = (keyword, product) => {
  const stored = getStoredSelectedProducts();

  if (!stored[keyword]) {
    stored[keyword] = [];
  }

  // Check if product already exists
  const exists = stored[keyword].some(p => (p.sku || p.offerId) === (product.sku || product.offerId));

  if (!exists) {
    stored[keyword].push({ ...product, keyword });
    storeSelectedProducts(stored);
  }
};

/**
 * Remove product from stored selection for a specific keyword
 * @param {string} keyword - Search keyword
 * @param {string} productId - Product SKU or offerId
 */
export const removeProductFromStorage = (keyword, productId) => {
  const stored = getStoredSelectedProducts();

  if (stored[keyword]) {
    stored[keyword] = stored[keyword].filter(p => (p.sku || p.offerId) !== productId);

    // Remove empty keyword groups
    if (stored[keyword].length === 0) {
      delete stored[keyword];
    }

    storeSelectedProducts(stored);
  }
};

/**
 * Remove entire keyword group from storage
 * @param {string} keyword - Keyword to remove
 */
export const removeKeywordFromStorage = (keyword) => {
  const stored = getStoredSelectedProducts();

  if (stored[keyword]) {
    delete stored[keyword];
    storeSelectedProducts(stored);
  }
};

/**
 * Check if a product is selected for a keyword
 * @param {string} keyword - Search keyword
 * @param {string} productId - Product SKU or offerId
 * @returns {boolean}
 */
export const isProductStoredAsSelected = (keyword, productId) => {
  const stored = getStoredSelectedProducts();
  const keywordGroup = stored[keyword];
  return keywordGroup ? keywordGroup.some(p => (p.sku || p.offerId) === productId) : false;
};

/**
 * Get total count of stored selected products
 * @returns {number}
 */
export const getStoredSelectedProductsCount = () => {
  const stored = getStoredSelectedProducts();
  return Object.values(stored).reduce((total, products) => total + products.length, 0);
};